/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import { ContainerQuery } from 'react-container-query';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { createStructuredSelector } from 'reselect';
import injectSaga from 'utils/injectSaga';
import injectReducer from 'utils/injectReducer';
import { MEDIA_QUERIES } from 'containers/App/constants';
import { reportToSegment } from 'containers/App/actions';

import RequireAuth from 'containers/RequireAuth/Loadable';

import TemplatesPage from 'containers/TemplatesPage/Loadable';
import TemplatePreviewPage from 'containers/TemplatePreviewPage';
import SurveysPage from 'containers/SurveysPage/Loadable';

import ReferralPage from 'containers/ReferralPage/Loadable';
import NotFoundPage from 'containers/NotFoundPage/Loadable';
import StatementsPublicPage from 'containers/StatementsPublicPage/Loadable';
import SurveyTranslationPage from 'containers/SurveyTranslationPage/Loadable';
import CrosstabsPage from 'containers/CrosstabsPage/Loadable';

import globalReducer from 'containers/App/reducer';
import GenericErrorPopup from 'components/GenericErrorPopup/Loadable';
import ErrorBoundary from 'components/ErrorBoundary/Loadable';

import ConfirmModalGeneral from 'containers/ConfirmModal/Loadable';
import GeneralConfirmModal from 'containers/GeneralConfirmModal/Loadable';
import {
  SurveyRoutes,
  AccountRoutes,
} from 'containers/SurveysAccountsLayout/routes';

import SurveysAccountsLayout from 'containers/SurveysAccountsLayout/Loadable';

import { KEYBOARD } from 'utils/constants';
import { makeSelectUserDetails } from 'containers/User/selectors';
import saga from './saga';

/* eslint-disable react/prefer-stateless-function */
export class App extends React.Component {
  static propTypes = {
    reportToSegment: PropTypes.func.isRequired,
    userDetails: PropTypes.object.isRequired,
    isDistributionLink: PropTypes.bool.isRequired,
  };

  toggleAccessibleClass = (enabled) =>
    enabled
      ? document.body.classList.add('accessible-on')
      : document.body.classList.remove('accessible-on');

  tabListener = (e) => {
    const key = e.key || e.code || String.fromCharCode(e.keyCode);
    if (key === KEYBOARD.TAB || key === '\t') {
      this.toggleAccessibleClass(true);
    }
  };

  reportToSegmentAction = () => {
    this.props.reportToSegment(this.props.userDetails);
  };

  clickListener = () => {
    this.toggleAccessibleClass(false);
  };

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillMount() {
    document.addEventListener('keyup', this.tabListener);
    document.addEventListener('click', this.clickListener);
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.tabListener);
    document.removeEventListener('click', this.clickListener);
  }

  render() {
    const layout = (
      <div>
        <Switch>
          <Route exact path="/" render={() => <Redirect to="/surveys" />} />
          <Route
            exact
            path="/survey/translations/:surveyId"
            render={() => (
              <RequireAuth {...this.props}>
                <SurveyTranslationPage {...this.props} />
              </RequireAuth>
            )}
          />
          <Route
            path="/survey/:surveyId/cross-tabs/:crosstabId?"
            render={() => (
              <RequireAuth {...this.props}>
                <CrosstabsPage {...this.props} />
              </RequireAuth>
            )}
          />
          <Route
            path={[
              ...AccountRoutes.map(({ path }) => path),
              ...SurveyRoutes.map(({ path }) => path),
            ]}
            render={() => (
              <RequireAuth {...this.props}>
                <SurveysAccountsLayout {...this.props} />
              </RequireAuth>
            )}
          />
          <Route path="/insights" render={() => <Redirect to="/surveys" />} />

          {/* OLD ROUTES */}
          <Route
            path="/surveys/:folderId?"
            render={() => (
              <RequireAuth {...this.props}>
                <SurveysPage {...this.props} />
              </RequireAuth>
            )}
          />
          <Route
            path="/templates/:folderId?"
            render={() => (
              <RequireAuth {...this.props}>
                <TemplatesPage {...this.props} />
              </RequireAuth>
            )}
          />
          <Route
            path="/template/:templateId"
            render={() => (
              <RequireAuth {...this.props}>
                <TemplatePreviewPage {...this.props} />
              </RequireAuth>
            )}
          />
          <Route
            path="/survey/ai-survey"
            render={() => <Redirect to="/surveys" />}
          />
          <Route
            path="/account/referral"
            render={() => (
              <RequireAuth {...this.props}>
                <ReferralPage {...this.props} />
              </RequireAuth>
            )}
          />
          <Route
            path="/pt/:id/:token"
            render={() => <StatementsPublicPage />}
          />
          <Route
            render={() => (
              <RequireAuth {...this.props}>
                <NotFoundPage {...this.props} />
              </RequireAuth>
            )}
          />
        </Switch>
      </div>
    );

    return (
      <ErrorBoundary reportToSegment={this.reportToSegmentAction}>
        <GenericErrorPopup />
        <ConfirmModalGeneral />
        <GeneralConfirmModal />
        <ContainerQuery query={MEDIA_QUERIES}>
          {(params) => <div className={classNames(params)}>{layout}</div>}
        </ContainerQuery>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  userDetails: makeSelectUserDetails,
});

export function mapDispatchToProps(dispatch) {
  return {
    reportToSegment: (userDetails) => dispatch(reportToSegment(userDetails)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withGlobalReducer = injectReducer({
  key: 'global',
  reducer: globalReducer,
});
const withSaga = injectSaga({ key: 'app', saga });

export default compose(
  withRouter,
  withGlobalReducer,
  withSaga,
  withConnect,
)(App);
